// src/components/WorkoutList.js
import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "../api";
import "./WorkoutList.css";
import ExerciseListPanel from "./ExerciseListPanel";
import WorkoutExerciseDetail from "./WorkoutExerciseDetail";

function useDebouncedCallback(fn, delay = 300) {
  const timer = useRef(null);
  const latest = useRef(fn);

  useEffect(() => {
    latest.current = fn;
  }, [fn]);

  const debounced = useCallback(
    (...args) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => latest.current(...args), delay);
    },
    [delay]
  );

  useEffect(() => () => clearTimeout(timer.current), []);
  return debounced;
}

export default function WorkoutList({ currentYmd, onHighlightUpdate }) {
  const [exercises, setExercises] = useState([]);
  const [highlightIds, setHighlightIds] = useState(new Set());
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const [muscleGroups, setMuscleGroups] = useState([]);
  const [filterIdx, setFilterIdx] = useState(0);

  const [activeExercise, setActiveExercise] = useState(null);
  const [sets, setSets] = useState([]);
  const [editTarget, setEditTarget] = useState(null);

  /* ---------------- Autosave plumbing ---------------- */
  const latestSetsRef = useRef([]);
  useEffect(() => {
    latestSetsRef.current = sets;
  }, [sets]);

  const saveTimerRef = useRef(null);
  const savingRef = useRef(false);
  const pendingSaveRef = useRef(false);

  const buildValidSets = useCallback((setsArr) => {
    return (setsArr || [])
      .filter((s) => s.reps !== "" || s.weight !== "")
      .map((s) => ({
        setIndex: s.setIndex,
        reps: s.reps === "" ? 0 : Number(s.reps),
        weight: s.weight === "" ? 0 : Number(s.weight),
      }));
  }, []);

  const doSaveSetsSilent = useCallback(
    async (exercise, setsArr) => {
      if (!exercise || !currentYmd) return;

      const validSets = buildValidSets(setsArr);
      if (validSets.length === 0) return;

      if (savingRef.current) {
        pendingSaveRef.current = true;
        return;
      }

      savingRef.current = true;
      try {
        await api(`/me/workout/${currentYmd}/${exercise.id}`, {
          method: "PUT",
          body: { sets: validSets },
          invalidatePrefixes: [`/me/workout-done?ymd=${currentYmd}`, `/me/workout/${currentYmd}/`],
        });

        setSets((prev) =>
          prev.map((s) => ({
            ...s,
            saved: s.reps !== "" || s.weight !== "",
          }))
        );
        setHighlightIds((prev) => new Set(prev).add(exercise.id));
      } catch (err) {
        console.error("Autosave failed:", err);
      } finally {
        savingRef.current = false;
        if (pendingSaveRef.current) {
          pendingSaveRef.current = false;
          doSaveSetsSilent(exercise, latestSetsRef.current);
        }
      }
    },
    [buildValidSets, currentYmd]
  );

  const scheduleAutoSave = useCallback(
    (exercise) => {
      if (!exercise) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

      saveTimerRef.current = setTimeout(() => {
        doSaveSetsSilent(exercise, latestSetsRef.current);
      }, 450);
    },
    [doSaveSetsSilent]
  );

  const flushAutoSave = useCallback(
    async (exercise) => {
      if (!exercise) return;
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      await doSaveSetsSilent(exercise, latestSetsRef.current);
    },
    [doSaveSetsSilent]
  );

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  /* ---------------- Load exercises + favorites + muscle groups once ---------------- */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const all = await api("/exercises", { cacheTtlMs: 6 * 60 * 60_000 });
        if (cancelled) return;

        const ex = Array.isArray(all) ? all : [];
        setExercises(ex);

        try {
          const mgRows = await api("/muscle-groups", { cacheTtlMs: 6 * 60 * 60_000 });
          if (cancelled) return;

          const groups = (Array.isArray(mgRows) ? mgRows : [])
            .map((r) => String(r?.muscle_group ?? r?.muscleGroup ?? "").trim())
            .filter(Boolean);

          setMuscleGroups(groups);
        } catch (err) {
          console.error("Failed to load muscle groups; falling back to exercises", err);

          const derived = Array.from(
            new Set(
              ex
                .map((e) => e?.muscleGroup?.group)
                .map((s) => (s ? String(s).trim() : ""))
                .filter(Boolean)
            )
          ).sort();

          if (!cancelled) setMuscleGroups(derived);
        }

        try {
          const fav = await api("/me/favorites", { cacheTtlMs: 10 * 60_000 });
          if (!cancelled && fav?.exerciseIds) setFavoriteIds(new Set(fav.exerciseIds));
        } catch (err) {
          console.error("Failed to load favorites data", err);
        }
      } catch (err) {
        console.error("Failed to load exercises", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------------- Track mode: load done for day ---------------- */
  useEffect(() => {
    let mounted = true;

    (async () => {
      if (!currentYmd) return;
      try {
        const res = await api(`/me/workout-done?ymd=${encodeURIComponent(currentYmd)}`, {
          cacheTtlMs: 5 * 60_000,
        });
        const exerciseIds = Array.isArray(res?.exerciseIds) ? res.exerciseIds : [];
        if (!mounted) return;
        setHighlightIds(new Set(exerciseIds));
      } catch (err) {
        console.error("Failed to load workout-done", err);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [currentYmd]);

  /* ---------------- MuscleMap highlight: debounced, skip duplicates ---------------- */
  const lastNamesKeyRef = useRef("");

  const debouncedHighlight = useDebouncedCallback(async (names) => {
    if (!onHighlightUpdate) return;

    const key = names.slice().sort().join("|");
    if (key === lastNamesKeyRef.current) return;
    lastNamesKeyRef.current = key;

    try {
      const res = await api("/highlight", {
        method: "POST",
        body: { selectedWorkoutNames: names },
      });

      const { primary = [], secondary = [] } = res || {};
      onHighlightUpdate({
        primary: new Set(primary),
        secondary: new Set(secondary),
      });
    } catch (err) {
      console.error("highlight fetch failed", err);
    }
  }, 250);

  useEffect(() => {
    if (!exercises.length) return;

    const names = exercises.filter((e) => highlightIds.has(e.id)).map((e) => e.name);

    if (names.length === 0) {
      if (lastNamesKeyRef.current === "") return;
      lastNamesKeyRef.current = "";
      onHighlightUpdate?.({ primary: new Set(), secondary: new Set() });
      return;
    }

    debouncedHighlight(names);
  }, [highlightIds, exercises, debouncedHighlight, onHighlightUpdate]);

  /* ---------------- Done toggle ---------------- */
  async function handleToggle(exerciseId) {
    const isMarked = highlightIds.has(exerciseId);
    if (!currentYmd) return;

    try {
      if (!isMarked) {
        await api("/me/workout-entry", {
          method: "PUT",
          body: { ymd: currentYmd, exerciseId, setIndex: 1 },
          invalidatePrefixes: [`/me/workout-done?ymd=${currentYmd}`, "/me/workout/"],
        });
      } else {
        await api("/me/workout-entry", {
          method: "DELETE",
          body: { ymd: currentYmd, exerciseId },
          invalidatePrefixes: [`/me/workout-done?ymd=${currentYmd}`, "/me/workout/"],
        });
      }

      setHighlightIds((prev) => {
        const next = new Set(prev);
        if (isMarked) next.delete(exerciseId);
        else next.add(exerciseId);
        return next;
      });
    } catch (err) {
      console.error("Toggle failed in track mode", err);
    }
  }

  /* ---------------- Favorites ---------------- */
  async function toggleFavorite(exerciseId) {
    const isFav = favoriteIds.has(exerciseId);
    try {
      if (isFav) {
        await api(`/me/favorite/${exerciseId}`, { method: "DELETE", invalidatePrefixes: ["/me/favorites"] });
      } else {
        await api(`/me/favorite/${exerciseId}`, { method: "PUT", invalidatePrefixes: ["/me/favorites"] });
      }

      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.delete(exerciseId);
        else next.add(exerciseId);
        return next;
      });
    } catch (err) {
      console.error("favorite toggle failed", err);
    }
  }

  /* ---------------- Open exercise detail ---------------- */
  async function openExerciseDetail(exercise) {
    try {
      setEditTarget(null);
      setActiveExercise(exercise);

      const endpoint = `/me/workout/${encodeURIComponent(currentYmd)}/${encodeURIComponent(exercise.id)}`;
      const data = await api(endpoint, { cacheTtlMs: 10_000 });

      let loadedSets = (Array.isArray(data) ? data : []).map((r, idx) => ({
        reps: r.reps ?? "",
        weight: r.weight ?? "",
        setIndex: r.setIndex ?? idx + 1,
        saved: true,
      }));

      const minRows = 3;
      if (loadedSets.length < minRows) {
        loadedSets = [
          ...loadedSets,
          ...Array.from({ length: minRows - loadedSets.length }, (_, i) => ({
            reps: "",
            weight: "",
            setIndex: loadedSets.length + i + 1,
            saved: false,
          })),
        ];
      }

      if (loadedSets.every((s) => s.reps !== "" || s.weight !== "")) {
        loadedSets.push({ reps: "", weight: "", setIndex: loadedSets.length + 1, saved: false });
      }

      setSets(loadedSets);
    } catch (err) {
      console.error("❌ Failed to load sets:", err);
      setSets([{ reps: "", weight: "", setIndex: 1, saved: false }]);
    }
  }

  const applyPickerValues = useCallback(
    (idx, reps, weight) => {
      setSets((prev) => {
        const updated = prev.map((s, i) => {
          if (i !== idx) return s;
          return {
            ...s,
            reps: reps === "" ? "" : Math.max(0, Number(reps)),
            weight: weight === "" ? "" : Math.max(0, Number(weight)),
            saved: false,
          };
        });

        if (updated.every((s) => s.reps !== "" || s.weight !== "")) {
          updated.push({ reps: "", weight: "", setIndex: updated.length + 1, saved: false });
        }

        latestSetsRef.current = updated;
        return updated;
      });

      if (activeExercise) scheduleAutoSave(activeExercise);
    },
    [activeExercise, scheduleAutoSave]
  );

  async function saveSets(exercise) {
    const validSets = buildValidSets(sets);
    if (validSets.length === 0) {
      alert("Please enter at least one set before saving.");
      return;
    }

    try {
      await api(`/me/workout/${currentYmd}/${exercise.id}`, {
        method: "PUT",
        body: { sets: validSets },
        invalidatePrefixes: [`/me/workout-done?ymd=${currentYmd}`, `/me/workout/${currentYmd}/`],
      });

      setSets((prev) =>
        prev.map((s) => ({
          ...s,
          saved: s.reps !== "" || s.weight !== "",
        }))
      );

      setHighlightIds((prev) => new Set(prev).add(exercise.id));
      setEditTarget(null);
      setActiveExercise(null);
    } catch (err) {
      console.error("Failed to save sets in track mode", err);
      alert("Error saving sets");
    }
  }

  async function removeExercise(exercise) {
    try {
      await api("/me/workout-entry", {
        method: "DELETE",
        body: { ymd: currentYmd, exerciseId: exercise.id },
        invalidatePrefixes: [`/me/workout-done?ymd=${currentYmd}`, `/me/workout/${currentYmd}/`],
      });

      setHighlightIds((prev) => {
        const next = new Set(prev);
        next.delete(exercise.id);
        return next;
      });

      setEditTarget(null);
      setActiveExercise(null);
    } catch (err) {
      console.error("Failed to remove exercise in track mode", err);
    }
  }

  /* ---------------- Render ---------------- */
  if (loading) {
    return (
      <div className="wl">
        <div className="panel panel--stretch glass">
          <h3>Exercises</h3>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (activeExercise) {
    return (
      <div className="wl">
        <WorkoutExerciseDetail
          activeExercise={activeExercise}
          sets={sets}
          editTarget={editTarget}
          onOpenPicker={(idx) => setEditTarget({ idx })}
          onClosePicker={async () => {
            await flushAutoSave(activeExercise);
            setEditTarget(null);
          }}
          onConfirmPicker={({ reps, weight }) => {
            if (!editTarget) return;
            applyPickerValues(editTarget.idx, reps, weight);
          }}
          onBack={async () => {
            await flushAutoSave(activeExercise);
            setEditTarget(null);
            setActiveExercise(null);
          }}
          onSave={() => saveSets(activeExercise)}
          onRemove={() => removeExercise(activeExercise)}
        />
      </div>
    );
  }

  return (
    <div className="wl">
      <ExerciseListPanel
        title="Exercises"
        exercises={exercises}
        muscleGroups={muscleGroups}
        filterIdx={filterIdx}
        onFilterIdxChange={setFilterIdx}
        markedIds={highlightIds}
        onToggleMarked={handleToggle}
        markedClassName="done"
        markTitle={(isMarked) => (isMarked ? "Mark as not done" : "Mark as done")}
        favoriteIds={favoriteIds}
        onToggleFavorite={toggleFavorite}
        showFavorites
        onItemClick={openExerciseDetail}
      />
    </div>
  );
}